import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ItemMenu } from '@/core/components/database/menu';
import { NavegacaoService } from '@/core/services/navegacao/navegacao.service';
import { IconComponent } from '@/shared/components/ui/icon';

// Classes completas: o Tailwind só gera as que aparecem inteiras no código.
const CLASSES_NAV_DESKTOP_EXPANDIDA =
  'sticky top-16 flex h-[calc(100vh-4rem)] w-[15.5rem] shrink-0 flex-col gap-0.5 overflow-y-auto bg-rfy-surface px-[0.75rem] py-[1rem] [border-right:1px_solid_var(--rfy-border)] transition-[width]';
const CLASSES_NAV_DESKTOP_RECOLHIDA =
  'sticky top-16 flex h-[calc(100vh-4rem)] w-[4.5rem] shrink-0 flex-col gap-0.5 overflow-y-auto bg-rfy-surface px-[0.75rem] py-[1rem] [border-right:1px_solid_var(--rfy-border)] transition-[width]';
const CLASSES_NAV_MOBILE_ABERTA =
  'fixed left-[0] top-16 bottom-[0] z-30 flex w-[15.5rem] flex-col gap-0.5 overflow-y-auto bg-rfy-surface px-[0.75rem] py-[1rem] [border-right:1px_solid_var(--rfy-border)] translate-x-[0] transition-transform';
const CLASSES_NAV_MOBILE_FECHADA =
  'fixed left-[0] top-16 bottom-[0] z-30 flex w-[15.5rem] flex-col gap-0.5 overflow-y-auto bg-rfy-surface px-[0.75rem] py-[1rem] [border-right:1px_solid_var(--rfy-border)] translate-x-[-100%] transition-transform';

const CLASSES_ROTULO_SECAO =
  'px-[0.75rem] pt-[0.25rem] pb-[0.5rem] text-[0.6875rem] font-semibold tracking-[0.08em] text-rfy-muted';
const CLASSES_SR_ONLY = 'sr-only';

const CLASSES_ITEM =
  'flex h-11 items-center gap-[0.75rem] rounded-controle px-[0.75rem] text-sm font-medium text-rfy-subdued no-underline hover:bg-rfy-subtle hover:text-rfy-foreground-strong';
const CLASSES_ITEM_ATIVO =
  'flex h-11 items-center gap-[0.75rem] rounded-controle px-[0.75rem] text-sm font-semibold text-rfy-foreground-strong no-underline bg-rfy-primary/15';
const CLASSES_ITEM_RECOLHIDO =
  'flex h-11 items-center justify-center gap-[0.75rem] rounded-controle px-[0] text-sm font-medium text-rfy-subdued no-underline hover:bg-rfy-subtle hover:text-rfy-foreground-strong';
const CLASSES_ITEM_RECOLHIDO_ATIVO =
  'flex h-11 items-center justify-center gap-[0.75rem] rounded-controle px-[0] text-sm font-semibold text-rfy-foreground-strong no-underline bg-rfy-primary/15';

const CLASSES_GRUPO =
  'flex h-11 w-full items-center gap-[0.75rem] rounded-controle px-[0.75rem] text-left text-sm font-medium text-rfy-subdued no-underline bg-[transparent] border-solid border-[0px] cursor-pointer hover:bg-rfy-subtle hover:text-rfy-foreground-strong';
const CLASSES_GRUPO_RECOLHIDO =
  'flex h-11 w-full items-center justify-center gap-[0.75rem] rounded-controle px-[0] text-sm font-medium text-rfy-subdued no-underline bg-[transparent] border-solid border-[0px] cursor-pointer hover:bg-rfy-subtle hover:text-rfy-foreground-strong';

const CLASSES_ROTULO = 'flex-1';
const CLASSES_ROTULO_DESTACADO = 'flex-1 font-semibold text-rfy-foreground-strong';
const CLASSES_CHEVRON = 'shrink-0 text-rfy-muted';
const CLASSES_CHEVRON_ABERTO = 'shrink-0 text-rfy-primary';

const CLASSES_SUBITEM =
  'flex h-10 items-center rounded-lg px-[0.75rem] text-sm text-rfy-subdued no-underline hover:bg-rfy-subtle hover:text-rfy-foreground-strong';
const CLASSES_SUBITEM_ATIVO =
  'flex h-10 items-center rounded-lg px-[0.75rem] text-sm font-semibold text-rfy-foreground-strong no-underline bg-rfy-primary/15';

const CLASSES_RECOLHER =
  'flex h-11 w-full shrink-0 items-center gap-[0.75rem] rounded-controle px-[0.75rem] bg-[transparent] border-solid border-[1px] border-rfy-border text-[0.8125rem] text-rfy-muted cursor-pointer hover:bg-rfy-subtle hover:text-rfy-foreground';
const CLASSES_RECOLHER_RECOLHIDO =
  'flex h-11 w-full shrink-0 items-center justify-center gap-[0.75rem] rounded-controle px-[0] bg-[transparent] border-solid border-[1px] border-rfy-border text-[0.8125rem] text-rfy-muted cursor-pointer hover:bg-rfy-subtle hover:text-rfy-foreground';

/** Sidebar do layout vertical: grupos expansíveis, modo recolhido e drawer no mobile. */
@Component({
  selector: 'app-sidenav',
  standalone: true,
  imports: [RouterLink, IconComponent],
  templateUrl: './sidenav.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Contents: o nav e o backdrop viram filhos diretos do flex do layout.
  host: { class: 'contents' },
})
export class SidenavComponent {
  protected readonly navegacao = inject(NavegacaoService);

  /** Recolhida só no desktop; no mobile o drawer sempre mostra o menu completo. */
  protected readonly recolhida = computed(() => !this.navegacao.mobile() && this.navegacao.sidenavRecolhida());

  /** Rótulos dos grupos abertos. */
  protected readonly gruposAbertos = signal<ReadonlySet<string>>(new Set());

  protected readonly classesNav = computed(() => {
    if (this.navegacao.mobile()) {
      return this.navegacao.drawerAberto() ? CLASSES_NAV_MOBILE_ABERTA : CLASSES_NAV_MOBILE_FECHADA;
    }
    return this.recolhida() ? CLASSES_NAV_DESKTOP_RECOLHIDA : CLASSES_NAV_DESKTOP_EXPANDIDA;
  });

  constructor() {
    // Abre o grupo que contém a rota atual, sem fechar os demais.
    effect(
      () => {
        const rota = this.navegacao.rotaAtiva();
        if (!rota) return;

        const grupo = this.navegacao.menu().find((item) => item.submenu?.some((sub) => sub.rota === rota));
        if (!grupo) return;

        this.gruposAbertos.update((atuais) => (atuais.has(grupo.rotulo) ? atuais : new Set([...atuais, grupo.rotulo])));
      },
      { allowSignalWrites: true }
    );
  }

  protected ativa(rota?: string): boolean {
    return !!rota && this.navegacao.rotaAtiva() === rota;
  }

  protected grupoAtivo(grupo: ItemMenu): boolean {
    return grupo.submenu?.some((sub) => this.ativa(sub.rota)) ?? false;
  }

  protected aberto(grupo: ItemMenu): boolean {
    return !this.recolhida() && this.gruposAbertos().has(grupo.rotulo);
  }

  protected idGrupo(indice: number): string {
    return 'sidenav-grupo-' + indice;
  }

  protected alternarGrupo(grupo: ItemMenu): void {
    if (this.recolhida()) {
      this.navegacao.definirSidenavRecolhida(false);
      this.gruposAbertos.update((atuais) => new Set([...atuais, grupo.rotulo]));
      return;
    }

    this.gruposAbertos.update((atuais) => {
      const proximos = new Set(atuais);
      if (proximos.has(grupo.rotulo)) proximos.delete(grupo.rotulo);
      else proximos.add(grupo.rotulo);
      return proximos;
    });
  }

  protected classesItem(ativa: boolean): string {
    if (this.recolhida()) return ativa ? CLASSES_ITEM_RECOLHIDO_ATIVO : CLASSES_ITEM_RECOLHIDO;
    return ativa ? CLASSES_ITEM_ATIVO : CLASSES_ITEM;
  }

  protected classesGrupo(): string {
    return this.recolhida() ? CLASSES_GRUPO_RECOLHIDO : CLASSES_GRUPO;
  }

  protected classesSubitem(ativa: boolean): string {
    return ativa ? CLASSES_SUBITEM_ATIVO : CLASSES_SUBITEM;
  }

  protected classesRotuloSecao(): string {
    return this.recolhida() ? CLASSES_SR_ONLY : CLASSES_ROTULO_SECAO;
  }

  protected classesRotulo(destacado: boolean): string {
    if (this.recolhida()) return CLASSES_SR_ONLY;
    return destacado ? CLASSES_ROTULO_DESTACADO : CLASSES_ROTULO;
  }

  protected classesChevron(aberto: boolean): string {
    if (this.recolhida()) return CLASSES_SR_ONLY;
    return aberto ? CLASSES_CHEVRON_ABERTO : CLASSES_CHEVRON;
  }

  protected classesRecolher(): string {
    return this.recolhida() ? CLASSES_RECOLHER_RECOLHIDO : CLASSES_RECOLHER;
  }
}
